import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Divider,
  Chip,
  Avatar,
  Badge,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  Delete as DeleteIcon,
  Policy as PolicyIcon,
  VerifiedUser as VerifiedUserIcon,
  Notifications as NotificationsIcon,
  Verified as VerifiedIcon,
  AccountCircle as AccountCircleIcon,
  SavedSearch as SavedSearchIcon,
  HourglassTop as HourglassTopIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import PageShell, { SurfaceCard } from '../../components/common/PageShell';
import VerifiedBadge from '../../components/common/VerifiedBadge';
import { GetUser } from '../../services/profile';
import { SAVED_SEARCHES_PATH } from '../../utils/savedSearch';

const APP_VERSION = '1.0.0';

// Person/GetUser → identityVerification: 0 Pending, 1 Approved, 2 Rejected, 3 UnderReview, 4 NotApplied
const ID_STATUS = { PENDING: 0, APPROVED: 1, REJECTED: 2, UNDER_REVIEW: 3, NOT_APPLIED: 4 };

const Profile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentUser, logout } = useAuth();
  const [logoutDialog, setLogoutDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Kimlik doğrulama durumu sunucudan (önceden e-posta alan adına bakan geçici kontrol vardı)
  const [identity, setIdentity] = useState({ loaded: false, status: null, verified: false });

  useEffect(() => {
    let alive = true;
    GetUser()
      .then((res) => {
        if (!alive || !res?.success || !res.data) return;
        const status = Number(res.data.identityVerification);
        const known = Number.isInteger(status) ? status : null;
        setIdentity({
          loaded: true,
          status: known,
          verified:
            res.data.isIdentityVerified === true ||
            (res.data.isIdentityVerified === undefined && known === ID_STATUS.APPROVED),
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const isUserVerified = () => identity.verified;
  const inReview =
    !identity.verified && (identity.status === ID_STATUS.PENDING || identity.status === ID_STATUS.UNDER_REVIEW);
  const showVerifyCta = identity.loaded && !identity.verified && !inReview;

  const menuItems = [
    { icon: <SavedSearchIcon />, text: t('savedSearch.menu'), onClick: () => navigate(SAVED_SEARCHES_PATH) },
    { icon: <PolicyIcon />, text: t('settings.privacyPolicy'), onClick: () => navigate('/privacy') },
    { icon: <SecurityIcon />, text: t('settings.editPassword'), onClick: () => navigate('/profile/change-password') },
    { icon: <EmailIcon />, text: t('settings.editEmail'), onClick: () => navigate('/profile/edit-email') },
    { icon: <PhoneIcon />, text: t('settings.editPhone'), onClick: () => navigate('/profile/edit-phone') },
    { icon: <PersonIcon />, text: t('settings.editMate'), onClick: () => navigate('/profile/edit-mate') },
    { icon: <VerifiedUserIcon />, text: t('settings.verification'), onClick: () => navigate('/profile/identity-verification') },
    { icon: <HelpIcon />, text: t('settings.support'), onClick: () => navigate('/support') },
    { icon: <NotificationsIcon />, text: t('settings.notificationSettings'), onClick: () => navigate('/notification-settings') },
  ];

  const handleLogout = () => {
    logout();
    setLogoutDialog(false);
    toast.success(t('settings.logoutSuccess'));
    navigate('/login');
  };

  const handleDeleteAccount = () => {
    setDeleteDialog(false);
    toast.success(t('settings.deleteSuccess'));
    navigate('/login');
  };

  return (
    <PageShell
      maxWidth="md"
      title={t('profile.title') || t('navigation.profile')}
      subtitle={currentUser?.email}
      icon={<AccountCircleIcon />}
    >
      <SurfaceCard sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Box sx={{ position: 'relative', display: 'inline-block', mb: 1.5 }}>
            <Avatar
              sx={{
                width: 96,
                height: 96,
                fontSize: 34,
                mx: 'auto',
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: 'primary.main',
                fontWeight: 800,
                border: `3px solid ${theme.palette.background.paper}`,
                boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
              }}
            >
              {currentUser?.firstName?.charAt(0)}
              {currentUser?.lastName?.charAt(0)}
            </Avatar>
            {isUserVerified() && (
              <Badge
                sx={{ position: 'absolute', bottom: 4, right: -2 }}
                badgeContent={
                  <Tooltip title={t('verifiedBadge.tooltip')}>
                    <VerifiedIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} aria-hidden />
                  </Tooltip>
                }
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h5" fontWeight={800}>
              {currentUser?.firstName} {currentUser?.lastName}
            </Typography>
            <VerifiedBadge verified={isUserVerified()} variant="chip" />
            {inReview && (
              <Chip
                icon={<HourglassTopIcon fontSize="small" />}
                label={t('verifiedBadge.inReview')}
                color="warning"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </Box>

        {showVerifyCta && (
          <Box
            role="region"
            aria-label={t('verifiedBadge.ctaTitle')}
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: 2.5,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
            }}
          >
            <VerifiedUserIcon color="primary" sx={{ fontSize: 32, display: { xs: 'none', sm: 'block' } }} aria-hidden />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={800}>{t('verifiedBadge.ctaTitle')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {identity.status === ID_STATUS.REJECTED ? t('verifiedBadge.ctaRejected') : t('verifiedBadge.ctaBody')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<VerifiedIcon />}
              onClick={() => navigate('/profile/identity-verification')}
              sx={{ flexShrink: 0 }}
            >
              {t('verifiedBadge.ctaButton')}
            </Button>
          </Box>
        )}

        <Divider sx={{ mb: 1.5 }} />

        <List disablePadding>
          {menuItems.map((item, index) => (
            <ListItemButton
              key={index}
              onClick={item.onClick}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 1.15,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              <ListItemIcon sx={{ color: 'primary.main', minWidth: 42 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
              />
            </ListItemButton>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Button
          fullWidth
          variant="contained"
          startIcon={<LogoutIcon />}
          onClick={() => setLogoutDialog(true)}
          sx={{ mb: 1.25, py: 1.15 }}
        >
          {t('settings.logout')}
        </Button>

        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteDialog(true)}
          sx={{ py: 1.1 }}
        >
          {t('settings.deleteAccount')}
        </Button>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
          Odazen v.{APP_VERSION}
        </Typography>
      </SurfaceCard>

      <Dialog open={logoutDialog} onClose={() => setLogoutDialog(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{t('settings.logoutConfirm')}</DialogTitle>
        <DialogContent>
          <Typography>{t('settings.logoutText')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialog(false)}>{t('settings.cancel')}</Button>
          <Button onClick={handleLogout} color="primary" variant="contained">
            {t('settings.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{t('settings.deleteConfirm')}</DialogTitle>
        <DialogContent>
          <Typography>{t('settings.confirmDeleteText')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>{t('settings.cancel')}</Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained">
            {t('settings.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default Profile;
